import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Alert,
  Tooltip,
  Progress,
  List,
  Avatar,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  BankOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  PlusOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import CompanyLegislationManager from "./CompanyLegislationManager";

const { Title, Text } = Typography;

interface CompanyLegislationSummary {
  id: number;
  company_id: number;
  company_name: string;
  company_short_name: string;
  legislation_count: number;
  has_primary: boolean;
  primary_legislation: string;
  primary_country: string;
  last_updated: string;
}

interface LegislationStats {
  total_companies: number;
  companies_with_legislation: number;
  companies_without_legislation: number;
  total_legislations: number;
  active_legislations: number;
  most_used_legislation: string;
  coverage_percentage: number;
}

const CompanyLegislationDashboard: React.FC = () => {
  const [stats, setStats] = useState<LegislationStats | null>(null);
  const [companySummaries, setCompanySummaries] = useState<
    CompanyLegislationSummary[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [showManager, setShowManager] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      // Récupérer les statistiques
      const [statsResponse, summariesResponse] = await Promise.all([
        fetch("/api/hr/tax-legislations/companies/stats", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
        fetch("/api/hr/tax-legislations/companies/summary", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
      ]);

      const statsData = await statsResponse.json();
      const summariesData = await summariesResponse.json();

      if (statsData.success) {
        setStats(statsData.data);
      }

      if (summariesData.success) {
        setCompanySummaries(summariesData.data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<CompanyLegislationSummary> = [
    {
      title: "Entreprise",
      key: "company",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.company_name}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.company_short_name}
          </Text>
        </div>
      ),
    },
    {
      title: "Législations assignées",
      key: "legislations",
      width: 150,
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            {record.legislation_count}
          </div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            législation(s)
          </Text>
        </div>
      ),
    },
    {
      title: "Législation principale",
      key: "primary",
      render: (_, record) => (
        <div>
          {record.has_primary ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                <span style={{ fontWeight: 500 }}>
                  {record.primary_country}
                </span>
              </div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {record.primary_legislation}
              </Text>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <WarningOutlined style={{ color: "#faad14" }} />
              <Text type="secondary">Aucune législation principale</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Statut",
      key: "status",
      width: 120,
      render: (_, record) => (
        <div>
          {record.legislation_count > 0 ? (
            record.has_primary ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                Configuré
              </Tag>
            ) : (
              <Tag color="warning" icon={<ExclamationCircleOutlined />}>
                Incomplet
              </Tag>
            )
          ) : (
            <Tag color="error" icon={<WarningOutlined />}>
              Non configuré
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Dernière mise à jour",
      dataIndex: "last_updated",
      key: "last_updated",
      width: 150,
      render: (date) => (
        <Text type="secondary">
          {date ? new Date(date).toLocaleDateString("fr-FR") : "Jamais"}
        </Text>
      ),
    },
  ];

  const getStatusColor = (percentage: number) => {
    if (percentage >= 80) return "#52c41a";
    if (percentage >= 60) return "#faad14";
    return "#ff4d4f";
  };

  if (showManager) {
    return (
      <div style={{ padding: "24px" }}>
        <div style={{ marginBottom: 16 }}>
          <Button onClick={() => setShowManager(false)}>
            ← Retour au tableau de bord
          </Button>
        </div>
        <CompanyLegislationManager />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* En-tête */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Tableau de bord des législations fiscales</Title>
        <Text type="secondary">
          Vue d'ensemble des assignations de législations fiscales par
          entreprise
        </Text>
      </div>

      {/* Statistiques principales */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total entreprises"
              value={stats?.total_companies || 0}
              prefix={<BankOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Entreprises configurées"
              value={stats?.companies_with_legislation || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Législations actives"
              value={stats?.active_legislations || 0}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <div style={{ textAlign: "center" }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Couverture globale</Text>
              </div>
              <Progress
                type="circle"
                percent={Math.round(stats?.coverage_percentage || 0)}
                size={80}
                strokeColor={getStatusColor(stats?.coverage_percentage || 0)}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Alertes */}
      {stats && stats.companies_without_legislation > 0 && (
        <Alert
          message="Entreprises non configurées"
          description={`${stats.companies_without_legislation} entreprise(s) n'ont pas de législation fiscale assignée. Cela peut affecter les calculs de paie.`}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => setShowManager(true)}
            >
              Configurer
            </Button>
          }
        />
      )}

      <Row gutter={16}>
        {/* Tableau des entreprises */}
        <Col xs={24} lg={16}>
          <Card
            title="État des assignations par entreprise"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowManager(true)}
              >
                Gérer les assignations
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={companySummaries}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} sur ${total} entreprises`,
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </Col>

        {/* Panneau latéral avec informations */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {/* Législation la plus utilisée */}
            <Card title="Législation la plus utilisée" size="small">
              <div style={{ textAlign: "center" }}>
                <Avatar
                  size={48}
                  icon={<GlobalOutlined />}
                  style={{ backgroundColor: "#1890ff", marginBottom: 8 }}
                />
                <div style={{ fontWeight: 500 }}>
                  {stats?.most_used_legislation || "Aucune"}
                </div>
              </div>
            </Card>

            {/* Actions rapides */}
            <Card title="Actions rapides" size="small">
              <List
                size="small"
                dataSource={[
                  {
                    title: "Gérer les assignations",
                    icon: <SettingOutlined />,
                    action: () => setShowManager(true),
                  },
                  {
                    title: "Voir les législations",
                    icon: <GlobalOutlined />,
                    action: () =>
                      (window.location.href = "/rh/taxes-legislation"),
                  },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <Button
                      type="link"
                      icon={item.icon}
                      onClick={item.action}
                      style={{ padding: 0 }}
                    >
                      {item.title}
                    </Button>
                  </List.Item>
                )}
              />
            </Card>

            {/* Conseils */}
            <Card title="Conseils" size="small">
              <List
                size="small"
                dataSource={[
                  "Chaque entreprise doit avoir une législation principale",
                  "Les calculs de paie utilisent la législation principale",
                  "Vérifiez régulièrement les mises à jour législatives",
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      • {item}
                    </Text>
                  </List.Item>
                )}
              />
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default CompanyLegislationDashboard;
